
import { useState } from 'react';

export default function SupportSection() {
  const [activeCategory, setActiveCategory] = useState<number | null>(null);

  const supportCategories = [
    {
      icon: 'ri-question-answer-line',
      title: 'General Questions',
      description: 'Get answers to frequently asked questions about ELTROM VPN',
      topics: [
        'How does ELTROM VPN protect my privacy?',
        'What devices are supported by ELTROM VPN?',
        'How many devices can I connect simultaneously?',
        'What is your no-log policy?'
      ]
    },
    {
      icon: 'ri-settings-line',
      title: 'Technical Support',
      description: 'Technical assistance for installation and configuration issues',
      topics: [
        'VPN connection troubleshooting',
        'Speed optimization tips',
        'Server selection guidance',
        'App installation support'
      ]
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Security & Privacy',
      description: 'Learn about our security features and privacy protection',
      topics: [
        'Understanding AES-256 encryption',
        'DNS leak protection',
        'Kill switch functionality',
        'IP address masking'
      ]
    }
  ];

  const handleCategoryClick = (index: number) => {
    setActiveCategory(activeCategory === index ? null : index);
  };

  return (
    <section id="support" className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-4">
            <div className="w-2 h-2 bg-[#00D9FF] rounded-full mr-3"></div>
            <span className="text-[#00D9FF] font-semibold uppercase tracking-wider text-sm">SUPPORT</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-[#0A1628] mb-6 leading-tight">
            How Can We<br />
            <span className="text-[#00D9FF]">Assist You?</span>
          </h2>
          <p className="text-lg text-[#6B7B93] max-w-2xl mx-auto leading-relaxed">
            Our dedicated support team is available 24/7 to help you with any questions 
            or technical issues you may encounter with ELTROM VPN.
          </p>
        </div>

        {/* Support Categories */}
        <div className="space-y-6">
          {supportCategories.map((category, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
            >
              <div 
                className="p-6 cursor-pointer flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                onClick={() => handleCategoryClick(index)}
              >
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-[#00D9FF] rounded-xl flex items-center justify-center">
                    <i className={`${category.icon} text-xl text-white`}></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#0A1628] mb-1">{category.title}</h3>
                    <p className="text-[#6B7B93]">{category.description}</p>
                  </div>
                </div>
                <div className={`transform transition-transform duration-200 ${activeCategory === index ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-2xl text-[#6B7B93]"></i>
                </div>
              </div>

              {/* Expanded Content */}
              {activeCategory === index && (
                <div className="px-6 pb-6">
                  <div className="border-t border-gray-100 pt-6">
                    <h4 className="font-semibold text-[#0A1628] mb-4">Common Topics:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {category.topics.map((topic, topicIndex) => (
                        <div 
                          key={topicIndex}
                          className="flex items-center space-x-3 p-3 bg-[#F7F9FC] rounded-xl hover:bg-[#EDF2F7] transition-colors duration-200 cursor-pointer"
                        >
                          <i className="ri-check-line text-[#00D9FF]"></i>
                          <span className="text-[#6B7B93] text-sm">{topic}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact Support CTA */}
        <div className="mt-16 bg-gradient-to-r from-[#00D9FF] to-[#0099FF] rounded-3xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">Need Immediate Help?</h3>
          <p className="mb-6 opacity-90">
            Can't find what you're looking for? Our expert support team is ready to assist you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-white text-[#00D9FF] px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-line mr-2"></i>
              Email Support
            </button>
            <button 
              onClick={() => {
                const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
                if (widget) widget.click();
              }}
              className="bg-black text-white px-6 py-3 rounded-xl font-semibold hover:bg-gray-800 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-chat-3-line mr-2"></i>
              Live Chat
            </button>
          </div>
        </div>

        {/* FAQ Preview */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold text-[#0A1628] text-center mb-8">Quick Answers</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                question: 'Is ELTROM VPN safe to use?',
                answer: 'Yes, ELTROM VPN uses military-grade AES-256 encryption and maintains a strict no-log policy to ensure your complete privacy and security.'
              },
              {
                question: 'How fast is ELTROM VPN?',
                answer: 'Our optimized global server network provides lightning-fast speeds with minimal impact on your internet connection performance.'
              },
              {
                question: 'Can I use ELTROM VPN on multiple devices?',
                answer: 'Yes, ELTROM VPN supports multiple devices including Windows, Mac, iOS, and Android with simultaneous connections.'
              },
              {
                question: 'Do you offer customer support?',
                answer: 'We provide 24/7 expert customer support through email, live chat, and comprehensive documentation to assist you.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-[#F7F9FC] rounded-2xl p-6">
                <h4 className="font-semibold text-[#0A1628] mb-3">{faq.question}</h4>
                <p className="text-[#6B7B93] text-sm leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
